import type { ReactNode } from 'react';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';

const leftRoundBorders = '3px 0 0 3px';
const rightRoundBorders = '0 3px 3px 0';

interface ButtonProps {
  className: string;
  children: ReactNode;
  color?: string;
  isSelected?: boolean;
}

const buttonStyles = (color?: string, isSelected?: boolean) => css`
  align-items: center;
  background-color: #fff;
  border: 1px solid ${etiColors.grayScale[7]};
  border-radius: 0;
  border-right: none;
  color: ${etiColors.black};
  cursor: pointer;
  display: flex;
  flex: 1;
  font-family: inherit;
  gap: 12px;
  height: 48px;
  justify-content: flex-start;

  @media ${mediaQueries.large.up} {
    height: 60px;
  }

  &:first-child {
    border-radius: ${leftRoundBorders};
  }

  &:last-child {
    border-radius: ${rightRoundBorders};
    border-right: 1px solid ${isSelected ? `${color}` : `${etiColors.grayScale[7]}`};
  }

  &:focus {
    outline: none;
  }

  [dir='rtl'] & {
    border-left: none;
    border-right: 1px solid ${isSelected ? `${color}` : `${etiColors.grayScale[7]}`};

    &:first-child {
      border-radius: ${rightRoundBorders};
    }

    &:last-child {
      border-left: 1px solid ${isSelected ? `${color}` : `${etiColors.grayScale[7]}`};
      border-radius: ${leftRoundBorders};
    }
  }
`;

export const Button = ({ className, children, color, isSelected, ...rest }: ButtonProps) => (
  <button className={cx(buttonStyles(color, isSelected), className)} type="button" {...rest}>
    {children}
  </button>
);

const verticalContainerStyles = css`
  display: flex;
  flex-direction: column;
`;

export const VerticalContainer = ({ children }: { children: ReactNode }) => (
  <div className={verticalContainerStyles}>{children}</div>
);

const commonLabelStyle = css`
  line-height: 1.3em;
  max-width: 65px;
  overflow: hidden;
  text-align: start;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media ${mediaQueries.medium.up} {
    max-width: 120px;
  }

  @media ${mediaQueries.large.up} {
    max-width: 200px;
  }
`;

const labelStyles = css`
  font-size: 0.625rem;

  @media ${mediaQueries.large.up} {
    font-size: 0.75rem;
  }
  ${commonLabelStyle}
`;

export const Label = ({
  className,
  children,
  ...rest
}: {
  className: string;
  children?: ReactNode;
}) => (
  <span className={cx(labelStyles, className)} {...rest}>
    {children}
  </span>
);

const priceStyles = css`
  font-size: 0.75rem;

  @media ${mediaQueries.large.up} {
    font-size: 0.875rem;
  }

  font-weight: 700;
  ${commonLabelStyle}
`;

export const Price = ({ className, ...rest }: { className: string }) => (
  <span className={cx(priceStyles, className)} {...rest} />
);
